/**
 * React Query hooks for processing rules management.
 */

import { useAuth } from "@/context/AuthContext";
import {
  createRule,
  deleteRule,
  getRule,
  listRules,
  searchCarriers,
  searchCustomers,
  updateRule,
} from "@/services/rulesApi";
import { ProcessingRule, RuleCreateData, RuleFilters, RuleUpdateData } from "@/types/rules";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchAuthSession } from "aws-amplify/auth";

async function getAccessToken(): Promise<string> {
  const session = await fetchAuthSession();
  const accessToken = session.tokens?.accessToken?.toString();
  if (!accessToken) {
    throw new Error("No access token available");
  }
  return accessToken;
}

export function useRules(filters?: RuleFilters) {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ["rules", filters],
    queryFn: async () => {
      const token = await getAccessToken();
      return listRules(token, filters);
    },
    enabled: isAuthenticated,
    staleTime: 30000,
  });
}

export function useInfiniteRules(filters?: Omit<RuleFilters, "limit" | "offset">) {
  const { isAuthenticated } = useAuth();
  const pageSize = 50;

  return useInfiniteQuery({
    queryKey: ["rules-infinite", filters],
    queryFn: async ({ pageParam = 0 }) => {
      const token = await getAccessToken();
      return listRules(token, {
        ...filters,
        limit: pageSize,
        offset: pageParam,
      });
    },
    getNextPageParam: (lastPage, allPages) => {
      const loadedCount = allPages.reduce((sum, page) => sum + page.rules.length, 0);
      if (loadedCount >= lastPage.total) {
        return undefined;
      }
      return loadedCount;
    },
    initialPageParam: 0,
    enabled: isAuthenticated,
    staleTime: 30000,
  });
}

export function useRule(ruleId: number | undefined) {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ["rule", ruleId],
    queryFn: async () => {
      if (!ruleId) throw new Error("Rule ID is required");
      const token = await getAccessToken();
      return getRule(token, ruleId);
    },
    enabled: isAuthenticated && !!ruleId,
  });
}

export function useCreateRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: RuleCreateData) => {
      const token = await getAccessToken();
      return createRule(token, data);
    },
    onSuccess: (newRule) => {
      queryClient.invalidateQueries({ queryKey: ["rules"] });
      queryClient.setQueryData<ProcessingRule>(["rule", newRule.id], newRule);
    },
  });
}

interface RulesListResponse {
  rules: ProcessingRule[];
  total: number;
}

export function useUpdateRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ruleId, data }: { ruleId: number; data: RuleUpdateData }) => {
      const token = await getAccessToken();
      return updateRule(token, ruleId, data);
    },
    onMutate: async ({ ruleId, data }) => {
      await queryClient.cancelQueries({ queryKey: ["rules"] });
      const previousRules = queryClient.getQueriesData<RulesListResponse>({ queryKey: ["rules"] });

      queryClient.setQueriesData<RulesListResponse>({ queryKey: ["rules"] }, (old) => {
        if (!old?.rules) return old;
        return {
          ...old,
          rules: old.rules.map((rule) =>
            rule.id === ruleId ? { ...rule, ...data, updated_at: new Date().toISOString() } : rule
          ),
        };
      });

      return { previousRules };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousRules) {
        context.previousRules.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSuccess: (updatedRule) => {
      queryClient.setQueryData<ProcessingRule>(["rule", updatedRule.id], updatedRule);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["rules"] });
    },
  });
}

export function useDeleteRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ruleId: number) => {
      const token = await getAccessToken();
      return deleteRule(token, ruleId);
    },
    onSuccess: (_, ruleId) => {
      queryClient.invalidateQueries({ queryKey: ["rules"] });
      queryClient.removeQueries({ queryKey: ["rule", ruleId] });
    },
  });
}

export function useSearchCarriers(search?: string, enabled: boolean = true) {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ["carriers", search],
    queryFn: async () => {
      const token = await getAccessToken();
      return searchCarriers(token, search);
    },
    enabled: isAuthenticated && enabled,
    staleTime: 60000,
  });
}

export function useSearchCustomers(search?: string, enabled: boolean = true) {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ["customers", search],
    queryFn: async () => {
      const token = await getAccessToken();
      return searchCustomers(token, search);
    },
    enabled: isAuthenticated && enabled,
    staleTime: 60000,
  });
}
